"use client";;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:ENCON_SENTRY_KEY":true});}catch(e){}}();

import { Linkedin, Play } from "lucide-react";
import Image from "next/image";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { useTranslations } from "next-intl";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface LinkedInPost {
  id: string;
  text: string;
  author: string;
  createdAt: string;
  lastModified?: string | null;
  edited?: boolean;
  imageUrl?: string | null;
  documentUrl?: string | null;
  article?: {
    thumbnail: string;
    title: string;
    description: string;
  } | null;
  permalink: string;
  shareMediaCategory?: string | null;
}

interface LinkedInCarouselProps {
  posts: LinkedInPost[];
  className?: string;
}

function parseLinkedInText(text: string): string {
  return (
    text
      // {hashtag|\#|TAG} → #TAG
      .replace(/\{hashtag\|\\#\|([^}]+)\}/g, "#$1")
      // @[Display Name](urn:li:...) → Display Name
      .replace(/@\[([^\]]+)\]\(urn:li:[^)]+\)/g, "$1")
      // Unescape LinkedIn escaped chars: \( \) \_ \#
      .replace(/\\([()_#])/g, "$1")
  );
}

export const LinkedInCarousel: React.FC<LinkedInCarouselProps> = ({
  posts,
  className = "",
}) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const t = useTranslations("LinkedIn");

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
      }
    };
  }, []);

  if (!posts || posts.length === 0) {
    return (
      <div className="linkedin-carousel__empty">
        <p>{t("noPostsAvailable")}</p>
        <a
          href="https://www.linkedin.com/company/enconduurzametransitie/posts/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("viewLinkedInPage")}
        </a>
      </div>
    );
  }

  return (
    <div className={`linkedin-carousel ${className}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={26}
        slidesPerView={1}
        pagination={{ clickable: true }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        breakpoints={{
          640: {
            slidesPerView: 1.5,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: 3,
            spaceBetween: 30,
          },
          1024: {
            slidesPerView: 3.5,
            spaceBetween: 30,
          },
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {posts.map((post) => (
          <SwiperSlide key={post.id}>
            <LinkedInPostCard post={post} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

interface LinkedInPostCardProps {
  post: LinkedInPost;
}

const LinkedInPostCard: React.FC<LinkedInPostCardProps> = ({ post }) => {
  const t = useTranslations("LinkedIn");
  const [imgError, setImgError] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("nl-BE", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return t("today");
    if (diffInDays === 1) return t("dayAgo");
    if (diffInDays < 7) return t("daysAgo", { days: diffInDays });
    if (diffInDays >= 7 && diffInDays <= 14)
      return t("weekAgo", { weeks: Math.floor(diffInDays / 7) });
    if (diffInDays < 30)
      return t("weeksAgo", { weeks: Math.floor(diffInDays / 7) });
    if (diffInDays >= 30 && diffInDays <= 60)
      return t("monthAgo", { months: Math.floor(diffInDays / 30) });
    if (diffInDays < 365)
      return t("monthsAgo", { months: Math.floor(diffInDays / 30) });
    return formatDate(dateString);
  };

  const truncateText = (text: string, maxLength: number = 300) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const hasMedia =
    (post.imageUrl && !imgError) ||
    post.article ||
    (post.shareMediaCategory === "DOCUMENT" && post.documentUrl);

  return (
    <a
      href={post.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className={`linkedin-post-card${!hasMedia ? " linkedin-post-card--text-only" : ""}`}
    >
      {/* Header with date */}
      <div className="linkedin-post-card__header">
        <div className="linkedin-post-card__header-flex">
          <Image
            src="/assets/Encon_logo.png"
            alt="Encon Logo"
            width={24}
            height={24}
          />
          <div className="linkedin-post-card__header-date">
            <span>{t("encon")}</span>
            <span className="time">{getRelativeTime(post.createdAt)}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="linkedin-post-card__content">
        <p>{parseLinkedInText(post.text)}</p>
      </div>
      {post.shareMediaCategory === "DOCUMENT" && post.documentUrl ? (
        <div className="linkedin-post-card__image linkedin-post-card__image--pdf">
          <iframe
            src={`/api/linkedin/document?url=${encodeURIComponent(post.documentUrl)}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title="Document preview"
            loading="lazy"
          />
        </div>
      ) : post.shareMediaCategory === "DOCUMENT" ? (
        <div className="linkedin-post-card__image linkedin-post-card__image--document">
          <Linkedin size={64} />
        </div>
      ) : post.imageUrl && !imgError ? (
        <div className="linkedin-post-card__image">
          <Image
            src={post.imageUrl}
            alt="LinkedIn post"
            width={400}
            height={400}
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            onError={() => setImgError(true)}
          />
          {post.shareMediaCategory === "VIDEO" && (
            <div className="linkedin-post-card__image-play">
              <Play size={40} />
            </div>
          )}
        </div>
      ) : null}

      {/* Footer */}
      <div className="linkedin-post-card__footer">
        <span className="linkedin-post-card__footer-link">
          {t("readMore")} →
        </span>
      </div>
    </a>
  );
};

export default LinkedInCarousel;
